import { useRef } from 'react';
import {
  useGetAhuDrawingQuery,
  useGetAhuDrawingCmsQuery,
  usePatchAhuDrawingMutation,
} from 'app/services/ahuBoardFormsApi';

/**
 * Phase 6e1 — Drawing window / title block (§8.3, drawing.info.js +
 * drawParams.tmpl markup faithful): 15 tabindexed fields (incl. the Callout
 * Schedule block: include checkbox and Left/Right side radios — the details
 * checkbox went when the description column became unconditional),
 * readonly UOM/Tolerance from the CMS defaults, notes >60 chars grow tooltips,
 * `.last-one` blur loops focus to `.first-input`, Enter on checkbox = click.
 *
 * QA batch 4 #8/#9 — inputs are UNCONTROLLED and the form renders only after
 * model+cms fetch, exactly like legacy (drawing.info.js renders once after
 * Promise.all(model, cms); `change input` → model.save PATCH). The old port
 * keyed each text input on its own value and PATCHed per keystroke: every
 * PATCH response remounted the focused input (typing lost focus after one
 * char), and checkboxes/radios were controlled by the RTKQ cache so they only
 * flipped after the PATCH round-trip. Text commits now fire on blur/Enter
 * (the DOM `change` cadence); checkboxes/radios flip natively and save in the
 * background — legacy Backbone `model.save` semantics (no rollback on fail).
 *
 * `hybridStandardLock` — the board is view-only *only* because a Standard user
 * opened a hybrid AHU (useAhuPermissions). Settings + Callout Schedule drive
 * Model View generation rather than the AHU configuration that gate protects,
 * so they stay live: each gets the legacy `.clickable` escape hatch that
 * re-arms it under `body.ahu-view-permission` (styles.css), and CanvasToolbar
 * puts the same class on #dwg_param so the pane itself takes pointer events.
 * The Title Block is NOT part of that — re-arming the pane would otherwise let
 * its inputs and their clear (x) icons through, so they are pinned read-only
 * and the clear icons are dropped. Every other read-only cause (no `ahu` write
 * permission, network read-only, critical task started) leaves the whole pane
 * locked exactly as before.
 */

interface DrawingWindowProps {
  ahuId: number;
  hybridStandardLock?: boolean;
}

const DrawingWindow = ({ ahuId, hybridStandardLock }: DrawingWindowProps) => {
  const { data: model } = useGetAhuDrawingQuery(ahuId);
  const { data: cmsDrawing } = useGetAhuDrawingCmsQuery();
  const [patchDrawing] = usePatchAhuDrawingMutation();
  const formRef = useRef<HTMLFormElement | null>(null);
  // last committed value per text field (change-event semantics: only save
  // when the value actually changed since the previous commit).
  const committed = useRef<Record<string, string>>({});

  const save = (name: string, value: unknown) => {
    void patchDrawing({ ahuId, data: { [name]: value } });
  };
  // §8.3's `.last-one` blur loops focus back to `.first-input`. The Callout
  // Schedule side radios are the form's last control, and a radio group is a
  // single Tab stop — whichever radio is checked is the one Tab reaches — so
  // the loop has to sit on both of them. Only redirect when focus actually
  // LEAVES the form: a blur to the other radio, or to a note field the user
  // clicked, is still inside it, and stealing focus there would drop their
  // next keystroke into Drawing Title.
  const loopFocusToFirstInput = (event: React.FocusEvent<HTMLInputElement>) => {
    const next = event.relatedTarget as HTMLElement | null;
    if (next && formRef.current?.contains(next)) return;
    formRef.current?.querySelector<HTMLInputElement>('input.first-input')?.focus();
  };
  const commitText = (name: string, value: string) => {
    const modelValue = String((model as Record<string, unknown> | undefined)?.[name] ?? '');
    const prev = name in committed.current ? committed.current[name] : modelValue;
    if (value === prev) return;
    committed.current[name] = value;
    save(name, value);
  };

  // Legacy renders once after BOTH fetches resolve (drawing.info.js
  // initialize) — uncontrolled defaultValues need the data at mount.
  if (!model || !cmsDrawing) return null;

  const cms = cmsDrawing as Record<string, unknown>;

  // Title Block stays read-only when the pane is re-armed for a hybrid-locked
  // Standard user (see the component docblock).
  const titleBlockLocked = !!hybridStandardLock;
  // The Settings / Callout Schedule controls opt back in via legacy's
  // `.clickable` escape hatch; no-op when the board isn't hybrid-locked.
  const liveClass = hybridStandardLock ? 'clickable' : '';

  const textField = (
    name: string,
    label: string,
    tabIndex: number,
    opts: { maxLength?: number; first?: boolean; note?: boolean } = {}
  ) => {
    const value = String((model as Record<string, unknown>)[name] ?? '');
    const noteTooltip = opts.note && value.length > 60;
    return (
      <>
        <div className="col-xs-4">
          <span className="name">{label}</span>
        </div>
        <div className={`col-xs-8${opts.note ? ' position-relative' : ''}`}>
          <input
            type="text"
            name={name}
            defaultValue={value}
            tabIndex={tabIndex}
            maxLength={opts.maxLength}
            readOnly={titleBlockLocked}
            className={`${opts.first ? 'first-input' : ''}${opts.note ? ' input-note' : ''}`}
            onBlur={(e) => commitText(name, e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitText(name, e.currentTarget.value);
            }}
          />
          {!titleBlockLocked && (
            <i
              className="text-clear icon-close-1"
              onClick={(e) => {
                const input = e.currentTarget.parentElement?.querySelector('input');
                if (input) input.value = '';
                commitText(name, '');
              }}
            />
          )}
          {opts.maxLength === 8 && (
            <>
              <span className="invalid-field-note minlength-note">Min 3 characters.</span>
              <span className="invalid-field-note maxlength-note">Max 8 characters.</span>
            </>
          )}
          {noteTooltip && <span className="note_tooltip">{value}</span>}
        </div>
      </>
    );
  };

  return (
    <form className="row" ref={formRef} onSubmit={(e) => e.preventDefault()}>
      <div className="col-12 title error-title border-bottom mb-10">Drawing</div>
      <div className="col-xs-12 sub-title">Title Block</div>
      {textField('title', 'Drawing Title', 1, { first: true })}
      {textField('description', 'Description', 2)}
      {textField('number', 'Drawing Number', 3)}
      <div className="col-xs-4">
        <span className="name">UOM</span>
      </div>
      <div className="col-xs-8">
        <input
          type="text"
          name="uom"
          value={String(cms.uom ?? '')}
          readOnly
          tabIndex={-1}
          onChange={() => undefined}
        />
      </div>
      {textField('scale', 'Scale', 4, { maxLength: 8 })}
      <div className="col-xs-4 tolerance">
        <span className="name">Tolerance</span>
        <span className="unit">+/-</span>
      </div>
      <div className="col-xs-8">
        <input
          type="text"
          name="tolerance"
          value={String(cms.tolerance ?? '')}
          readOnly
          tabIndex={-1}
          onChange={() => undefined}
        />
      </div>
      {textField('revision', 'Revision', 5)}
      {textField('drawn_by', 'Drawn by', 6)}
      {textField('checked_by', 'Checked by', 7)}
      <div className="col-xs-12 row position-relative">
        {textField('user_note_one', 'Drawing Note 1', 8, { maxLength: 80, note: true })}
      </div>
      <div className="col-xs-12 row position-relative">
        {textField('user_note_two', 'Drawing Note 2', 9, { maxLength: 80, note: true })}
      </div>
      <div className="col-xs-12 row position-relative">
        {textField('user_note_three', 'Drawing Note 3', 10, { maxLength: 80, note: true })}
      </div>
      <div className="col-xs-12 sub-title mt-20">Settings</div>
      <div className="col-xs-4">
        <span className="name">Show Section Lengths in Drawing</span>
      </div>
      <div className="col-xs-8">
        <input
          type="checkbox"
          tabIndex={11}
          className={liveClass}
          name="dwg_section_lengths"
          defaultChecked={!!model.dwg_section_lengths}
          onChange={(e) => save('dwg_section_lengths', e.currentTarget.checked)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.click();
          }}
        />
      </div>
      <div className="col-xs-4">
        <span className="name">Show Section Tags in Drawing</span>
      </div>
      <div className="col-xs-8">
        <input
          type="checkbox"
          tabIndex={12}
          className={liveClass}
          name="section_tags_in_dwg"
          defaultChecked={!!model.section_tags_in_dwg}
          onChange={(e) => save('section_tags_in_dwg', e.currentTarget.checked)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.click();
          }}
        />
      </div>
      <div className="col-xs-12 sub-title mt-20">Callout Schedule</div>
      <div className="col-xs-4">
        <span className="name">Include Callout Schedule in Drawing</span>
      </div>
      <div className="col-xs-8">
        <input
          type="checkbox"
          tabIndex={13}
          className={liveClass}
          name="include_callout_schedule"
          defaultChecked={!!model.include_callout_schedule}
          onChange={(e) => save('include_callout_schedule', e.currentTarget.checked)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.click();
          }}
        />
      </div>
      <div className="col-xs-4 callout-schedule-radio-row">
        <span className="name">Callout Schedule Location</span>
      </div>
      <div className="col-xs-8 callout-schedule-radio-row">
        <div className="row callout-schedule-radio-columns">
          <div className="col-xs-6">
            <label
              htmlFor="callout_schedule_side_left"
              className={`callout-schedule-radio-label ${liveClass}`.trim()}
            >
              <input
                type="radio"
                id="callout_schedule_side_left"
                tabIndex={14}
                className={`last-one ${liveClass}`.trim()}
                name="callout_schedule_side"
                value="left"
                defaultChecked={model.callout_schedule_side !== 'right'}
                onChange={(e) => save('callout_schedule_side', e.currentTarget.value)}
                onBlur={loopFocusToFirstInput}
              />
              <span>Left</span>
            </label>
          </div>
          <div className="col-xs-6">
            <label
              htmlFor="callout_schedule_side_right"
              className={`callout-schedule-radio-label ${liveClass}`.trim()}
            >
              <input
                type="radio"
                id="callout_schedule_side_right"
                tabIndex={15}
                className={`last-one ${liveClass}`.trim()}
                name="callout_schedule_side"
                value="right"
                defaultChecked={model.callout_schedule_side === 'right'}
                onChange={(e) => save('callout_schedule_side', e.currentTarget.value)}
                onBlur={loopFocusToFirstInput}
              />
              <span>Right</span>
            </label>
          </div>
        </div>
      </div>
    </form>
  );
};

export default DrawingWindow;
